import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TWebhookSelectProps } from '@/console/features/webhooks/components/webhook-select/webhook-select.types';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadAllWebhookConfigurationsQuery } from '@/shared/redux/api/notifications/notifications-api';
import {
  ReadAllWebhookConfigurationsQuery,
  WebhookConfigurationFragment,
} from '@/shared/types/types.generated';

export const WebhookSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  webhookFilter = undefined,
  disableTags = true,
  createOption,
  getItemId,
  additionalOptions = [],
  onSelectChange,
  required,
  rtkQueryArgs,
  multiple = true,
  ...rest
}: TWebhookSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();

  const createDefaultOption = (webhook: WebhookConfigurationFragment) => {
    return {
      label: webhook.name,
      value: webhook.webhookConfigurationId,
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadAllWebhookConfigurationsQuery) => {
    if (!data) {
      return [];
    }

    const webhooks = data.readAllWebhookConfigurations.items;
    const validWebhooks = webhookFilter
      ? webhooks.filter((project) => webhookFilter(project))
      : webhooks;

    const options = validWebhooks.map((project) => _createOption(project));

    if (additionalOptions.length > 0) {
      return [...additionalOptions, ...options];
    }

    return options;
  };

  return (
    <AsyncSearchableSelect<
      TFieldValues,
      ReadAllWebhookConfigurationsQuery,
      TOptionShape
    >
      name={name}
      label={t('integrations:select_webhooks', { count: multiple ? 2 : 1 })}
      placeholder={t('integrations:select_webhooks', {
        count: multiple ? 2 : 1,
      })}
      disableTags={disableTags}
      multiple={multiple}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadAllWebhookConfigurationsQuery,
        transformData,
        rtkQueryArgs,
      }}
      onSelectChange={onSelectChange}
      required={required}
      {...rest}
    />
  );
};
